<template>
  <Drawer
    v-model:visible="isVisible"
    position="right"
    :modal="true"
    :dismissable="true"
    :style="{ width: '32rem', maxWidth: '100vw' }"
    :pt="{
      header: 'border-b border-surface-200 dark:border-surface-700',
      content: 'flex flex-col h-full min-h-0 overflow-hidden p-0',
    }"
    class="h-full relative"
    data-test-id="error-details-drawer"
  >
    <template #header>
      <div class="flex items-center gap-2">
        <i class="pi pi-exclamation-circle text-red-500"></i>
        <span class="font-semibold">{{ errorStore.errorTitle }}</span>
      </div>
    </template>

    <div v-if="errorDetails" class="h-full flex flex-col overflow-y-auto">
      <!-- Error Type Badge -->
      <div
        class="px-4 py-3 bg-red-50 dark:bg-red-900/20 border-b border-surface-200 dark:border-surface-700"
      >
        <div class="flex items-center justify-between">
          <span
            class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
            :class="errorTypeBadgeClass"
          >
            {{ errorDetails.errorType }}
          </span>
          <span
            v-if="errorDetails.statusCode"
            class="text-sm font-mono text-surface-600 dark:text-surface-400"
          >
            HTTP {{ errorDetails.statusCode }}
          </span>
        </div>
      </div>

      <!-- Main Content -->
      <div class="flex-1 p-4 space-y-4">
        <!-- Error Message -->
        <div>
          <h4
            class="text-sm font-medium text-surface-500 dark:text-surface-400 mb-1"
          >
            Message
          </h4>
          <p class="text-surface-900 dark:text-surface-100">
            {{ errorDetails.message }}
          </p>
        </div>

        <!-- Error Code -->
        <div v-if="errorDetails.code">
          <h4
            class="text-sm font-medium text-surface-500 dark:text-surface-400 mb-1"
          >
            Error Code
          </h4>
          <code
            class="text-sm font-mono bg-surface-100 dark:bg-surface-800 px-2 py-1 rounded"
          >
            {{ errorDetails.code }}
          </code>
        </div>

        <ErrorReferenceRow
          v-for="reference in errorStore.references"
          :key="reference.label"
          :reference="reference"
        />

        <!-- DataDog Trace ID -->
        <div
          v-if="errorDetails.traceId"
          class="bg-surface-50 dark:bg-surface-800 rounded-lg p-3"
        >
          <h4
            class="text-sm font-medium text-surface-500 dark:text-surface-400 mb-1 flex items-center gap-1"
          >
            <i class="pi pi-link text-xs"></i>
            Trace ID
          </h4>
          <div class="flex items-center gap-2">
            <code
              class="flex-1 text-sm font-mono text-primary-600 dark:text-primary-400 break-all cursor-pointer hover:underline"
              @click="copyTraceId"
              v-tooltip.top="'Click to copy'"
            >
              {{ errorDetails.traceId }}
            </code>
            <Button
              icon="pi pi-copy"
              variant="text"
              size="small"
              severity="secondary"
              @click="copyTraceId"
              v-tooltip.top="'Copy to clipboard'"
              aria-label="Copy trace ID"
            />
          </div>
          <p class="text-xs text-surface-500 dark:text-surface-400 mt-2">
            Share this ID with dev team to help with troubleshooting.
          </p>
        </div>

        <!-- Request Details -->
        <div v-if="errorDetails.url || errorDetails.method">
          <h4
            class="text-sm font-medium text-surface-500 dark:text-surface-400 mb-1"
          >
            Request
          </h4>
          <div
            class="bg-surface-100 dark:bg-surface-800 rounded p-2 font-mono text-sm"
          >
            <span
              v-if="errorDetails.method"
              class="text-blue-600 dark:text-blue-400 font-semibold"
            >
              {{ errorDetails.method.toUpperCase() }}
            </span>
            <span
              v-if="errorDetails.url"
              class="text-surface-700 dark:text-surface-300 break-all ml-2"
            >
              {{ errorDetails.url }}
            </span>
          </div>
        </div>

        <!-- Timestamp -->
        <div>
          <h4
            class="text-sm font-medium text-surface-500 dark:text-surface-400 mb-1"
          >
            Timestamp
          </h4>
          <p class="text-sm text-surface-700 dark:text-surface-300">
            {{ formatTimestamp(errorDetails.timestamp) }}
          </p>
        </div>

        <!-- Field Errors (for validation errors) -->
        <div
          v-if="errorDetails.fieldErrors && errorDetails.fieldErrors.length > 0"
        >
          <h4
            class="text-sm font-medium text-surface-500 dark:text-surface-400 mb-2"
          >
            Field Errors
          </h4>
          <div class="space-y-2">
            <div
              v-for="(fieldError, index) in errorDetails.fieldErrors"
              :key="index"
              class="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded p-2"
            >
              <div class="font-medium text-sm text-red-800 dark:text-red-200">
                {{ fieldError.field }}
              </div>
              <div class="text-sm text-red-600 dark:text-red-400">
                {{ fieldError.message }}
              </div>
              <div
                v-if="fieldError.code"
                class="text-xs text-red-500 dark:text-red-500 font-mono mt-1"
              >
                {{ fieldError.code }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Footer Actions -->
      <div
        class="px-4 py-3 border-t border-surface-200 dark:border-surface-700 bg-surface-50 dark:bg-surface-800"
      >
        <div class="flex justify-end">
          <Button
            label="Close"
            severity="secondary"
            size="small"
            @click="handleClose"
          />
        </div>
      </div>
    </div>

    <!-- Empty State -->
    <div v-else class="flex-1 flex items-center justify-center p-8 text-center">
      <div class="text-surface-500 dark:text-surface-400">
        <i class="pi pi-info-circle text-4xl mb-2"></i>
        <p>No error details available</p>
      </div>
    </div>
  </Drawer>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import Drawer from 'primevue/drawer'
import Button from 'primevue/button'
import { useToast } from 'primevue/usetoast'
import ErrorReferenceRow from '@/components/common/ErrorReferenceRow.vue'
import { useErrorDetailsStore } from '@/stores/errorDetailsStore'
import { successToast } from '@/util/toasts'

const toast = useToast()
const errorStore = useErrorDetailsStore()

const isVisible = computed({
  get: () => errorStore.isDrawerVisible,
  set: (value: boolean) => {
    if (!value) {
      errorStore.hideErrorDetails()
    }
  },
})

const errorDetails = computed(() => {
  if (!errorStore.currentError) return null
  return errorStore.currentError.toDetailedInfo()
})

const errorTypeBadgeClass = computed(() => {
  if (!errorDetails.value) return ''

  const type = errorDetails.value.errorType
  switch (type) {
    case 'Validation Error':
      return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300'
    case 'Authentication Error':
    case 'Authorization Error':
      return 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300'
    case 'Network Error':
      return 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300'
    case 'Server Error':
      return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
    case 'Not Found':
      return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300'
    default:
      return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
  }
})

function formatTimestamp(isoString: string): string {
  const date = new Date(isoString)
  return date.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    timeZoneName: 'short',
  })
}

async function copyTraceId() {
  if (!errorDetails.value?.traceId) return

  try {
    await navigator.clipboard.writeText(errorDetails.value.traceId)
    successToast(toast, 'Copied', 'Trace ID copied to clipboard')
  } catch {
    // Fallback for older browsers
    const textArea = document.createElement('textarea')
    textArea.value = errorDetails.value.traceId
    document.body.appendChild(textArea)
    textArea.select()
    document.execCommand('copy')
    document.body.removeChild(textArea)
    successToast(toast, 'Copied', 'Trace ID copied to clipboard')
  }
}

function handleClose() {
  errorStore.hideErrorDetails()
}
</script>
